/**
 * RecycleDialog.jsx
 * Adding a published post to the evergreen rotation.
 *
 * Three decisions, and only three: which rotation it joins, how many times it
 * may come round, and when it stops being true. The last two are the ones that
 * matter — an evergreen queue with no retirement rules is how a Christmas post
 * ends up going out in July, and asking now costs a moment whereas noticing
 * later costs the brand.
 */

import { useState } from 'react';
import { api } from '../services/api.js';
import { Check, Warning, Recycle } from './Icons.jsx';

export default function RecycleDialog({ post, categories = [], onClose, onAdded }) {
  const [category, setCategory] = useState(categories[0] ?? 'general');
  const [limitUses, setLimitUses] = useState(false);
  const [maxUses, setMaxUses] = useState(6);
  const [expires, setExpires] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const name = category.trim() || 'general';

  async function add() {
    setBusy(true);
    setError(null);
    try {
      await api.evergreen.add({
        post_id: post.post_id ?? post.id,
        category: name,
        max_uses: limitUses ? Number(maxUses) : null,
        expires_at: expires || null,
      });
      onAdded(name);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>
          <Recycle size={19} /> Bring this post round again
        </h2>

        <p className="hint">
          {(post.caption ?? '').slice(0, 140) || 'This post'}
        </p>

        {error && (
          <div className="notice notice--error">
            <Warning />
            <span>{error.message}</span>
          </div>
        )}

        <div className="field">
          <label htmlFor="rec-cat">Rotation</label>
          <input
            id="rec-cat"
            list="rec-categories"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="tips"
          />
          <datalist id="rec-categories">
            {categories.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
          <p className="hint">
            A rotation is a label with its own posting times — “tips”, “testimonials”, “offers”.
            Set those times on the Evergreen screen.
          </p>
        </div>

        <label className="checkline">
          <input
            type="checkbox"
            checked={limitUses}
            onChange={(e) => setLimitUses(e.target.checked)}
          />
          <span>
            <b>Retire it after a number of outings.</b>
            <span className="checkline__hint">
              Without this it comes round indefinitely, resting three weeks between each.
            </span>
          </span>
        </label>

        {limitUses && (
          <div className="field">
            <label htmlFor="rec-max">How many times</label>
            <input
              id="rec-max"
              type="number"
              min="1"
              max="100"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              style={{ maxWidth: 120 }}
            />
          </div>
        )}

        <div className="field">
          <label htmlFor="rec-expires">Stop using it after (optional)</label>
          <input
            id="rec-expires"
            type="date"
            value={expires}
            onChange={(e) => setExpires(e.target.value)}
            style={{ maxWidth: 200 }}
          />
          <p className="hint">
            For anything with a date attached — an offer, an event, a seasonal post.
          </p>
        </div>

        <div className="btn-row">
          <button className="btn btn--primary" onClick={add} disabled={busy}>
            <Check /> {busy ? 'Adding…' : `Add to “${name}”`}
          </button>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
